import React, { useEffect, useState } from 'react';
import { CheckCircle, CheckCircle2, Loader, XCircle, AlertTriangle, Play, Eye, ChevronDown, ChevronUp, Building2, MapPin, Tag, FileText } from 'lucide-react';
import { apiClient } from '../../config/api';
import Modal from '../../components/Modal';

const AdminDealershipOnboardingPage = () => {
    const [groups, setGroups] = useState([]);
    const [selectedGroupId, setSelectedGroupId] = useState('');
    const [loading, setLoading] = useState(true);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [onboarding, setOnboarding] = useState(false);
    const [creatingMessages, setCreatingMessages] = useState(false);
    const [pushingToVoicebox, setPushingToVoicebox] = useState(false);
    const [preview, setPreview] = useState(null);
    const [onboardingResults, setOnboardingResults] = useState(null);
    const [createMessagesResults, setCreateMessagesResults] = useState(null);
    const [pushToVoiceboxResults, setPushToVoiceboxResults] = useState(null);
    const [overwriteExisting, setOverwriteExisting] = useState(false);
    const [showOnboardConfirm, setShowOnboardConfirm] = useState(false);
    const [showPushConfirm, setShowPushConfirm] = useState(false);
    const [expandedLocations, setExpandedLocations] = useState(new Set());

    useEffect(() => {
        const loadGroups = async () => {
            try {
                setLoading(true);
                const groupsResult = await apiClient.get('/groups');
                const sortedGroups = (groupsResult || []).sort((a, b) => a.name.localeCompare(b.name));
                setGroups(sortedGroups);
            } catch (error) {
                console.error('Failed to load groups:', error);
            } finally {
                setLoading(false);
            }
        };

        loadGroups();
    }, []);

    useEffect(() => {
        if (selectedGroupId) {
            loadPreview();
        } else {
            setPreview(null);
            setOnboardingResults(null);
        }
    }, [selectedGroupId]);

    const loadPreview = async () => {
        if (!selectedGroupId) return;

        setLoadingPreview(true);
        try {
            const previewData = await apiClient.get(`/voicebox/groups/${selectedGroupId}/onboard/preview`);
            setPreview(previewData);
            setOnboardingResults(null);
        } catch (error) {
            console.error('Failed to load preview:', error);
            setPreview(null);
        } finally {
            setLoadingPreview(false);
        }
    };

    const handleCreateMessages = async () => {
        if (!selectedGroupId) return;

        setCreatingMessages(true);
        setCreateMessagesResults(null);

        try {
            const results = await apiClient.post(`/voicebox/groups/${selectedGroupId}/create-messages`, {
                overwrite: overwriteExisting,
                skip_validation: false
            });

            setCreateMessagesResults(results);
            // Reload preview to show updated state
            await loadPreview();
        } catch (error) {
            console.error('Create messages failed:', error);
            setCreateMessagesResults({
                success: false,
                error: error.response?.data?.error || error.message || 'Failed to create messages',
                validationErrors: error.response?.data?.validationErrors || [],
                warnings: error.response?.data?.warnings || []
            });
        } finally {
            setCreatingMessages(false);
        }
    };

    const handlePushToVoicebox = async () => {
        if (!selectedGroupId) return;

        setPushingToVoicebox(true);
        setPushToVoiceboxResults(null);
        setShowPushConfirm(false);

        try {
            const results = await apiClient.post(`/voicebox/groups/${selectedGroupId}/push-to-voicebox`, {
                folder_prefix: 'Onboarding',
                overwrite: overwriteExisting,
                confirm_push: true
            });

            setPushToVoiceboxResults(results);
            // Reload preview to show updated state
            await loadPreview();
        } catch (error) {
            console.error('Push to VoiceBox failed:', error);
            setPushToVoiceboxResults({
                success: false,
                error: error.response?.data?.error || error.message || 'Failed to push to VoiceBox',
                validationErrors: error.response?.data?.validationErrors || [],
                warnings: error.response?.data?.warnings || []
            });
        } finally {
            setPushingToVoicebox(false);
        }
    };

    const handleOnboard = async () => {
        if (!selectedGroupId) return;

        setOnboarding(true);
        setOnboardingResults(null);
        setShowOnboardConfirm(false);

        try {
            const results = await apiClient.post(`/voicebox/groups/${selectedGroupId}/onboard`, {
                folder_prefix: 'Onboarding',
                overwrite: overwriteExisting,
                skip_validation: false,
                confirm_push: true // Explicit confirmation to push to VoiceBox
            });

            setOnboardingResults(results);
            // Reload preview to show updated state
            await loadPreview();
        } catch (error) {
            console.error('Onboarding failed:', error);
            setOnboardingResults({
                success: false,
                error: error.response?.data?.error || error.message || 'Onboarding failed',
                validationErrors: error.response?.data?.validationErrors || [],
                warnings: error.response?.data?.warnings || []
            });
        } finally {
            setOnboarding(false);
        }
    };

    const toggleLocationExpanded = (locationId) => {
        const newExpanded = new Set(expandedLocations);
        if (newExpanded.has(locationId)) {
            newExpanded.delete(locationId);
        } else {
            newExpanded.add(locationId);
        }
        setExpandedLocations(newExpanded);
    };

    const hasValidationErrors = preview?.validationErrors?.length > 0;
    const hasWarnings = preview?.warnings?.length > 0;
    const canOnboard = preview && !hasValidationErrors && preview.totalLocations > 0 && preview.totalTemplates > 0;

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="max-w-7xl mx-auto">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">Dealership Onboarding</h1>
                    <p className="text-gray-600 mb-3">
                        Automatically generate location-specific messages for all locations in a dealership group.
                        The system will loop through each location, get their associated brands, and create messages from brand templates.
                    </p>
                    <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-md mb-3">
                        <div className="flex items-start">
                            <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                            <div>
                                <p className="font-semibold text-green-900 mb-1">Two-Step Process Available</p>
                                <p className="text-sm text-green-800">
                                    You can now <strong>create messages locally first</strong> (Step 1), then <strong>push them to VoiceBox</strong> (Step 2) 
                                    when the client database is ready. This prevents sync errors when the client doesn't exist yet in VoiceBox.
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-md">
                        <div className="flex items-start">
                            <AlertTriangle className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                            <div>
                                <p className="font-semibold text-blue-900 mb-1">Important: Multiple Brands Per Location</p>
                                <p className="text-sm text-blue-800">
                                    A location can have <strong>multiple brands</strong> associated with it. For each location, 
                                    the system will process <strong>all associated brands</strong> and create separate messages 
                                    for each brand using the appropriate brand templates. This means if a location sells both 
                                    VW and Audi, it will receive messages for both brands.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Group Selection */}
                <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select Dealership Group
                    </label>
                    <div className="relative">
                        <select
                            value={selectedGroupId}
                            onChange={(e) => setSelectedGroupId(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            disabled={loading || onboarding}
                        >
                            <option value="">-- Select a group --</option>
                            {groups.map(group => {
                                const isSynced = group.is_voicebox_synced === true;
                                return (
                                    <option key={group.id} value={group.id}>
                                        {group.name}{!isSynced && ' (Not Synced)'}
                                    </option>
                                );
                            })}
                        </select>
                        {selectedGroupId && (() => {
                            const selectedGroup = groups.find(g => g.id === selectedGroupId);
                            const isSynced = selectedGroup?.is_voicebox_synced === true;
                            return isSynced ? (
                                <div className="absolute right-10 top-1/2 transform -translate-y-1/2 pointer-events-none">
                                    <CheckCircle2 size={18} className="text-green-600" />
                                </div>
                            ) : null;
                        })()}
                    </div>
                </div>

                {/* Loading Preview */}
                {loadingPreview && (
                    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                        <div className="flex items-center justify-center">
                            <Loader className="animate-spin h-6 w-6 text-blue-600 mr-3" />
                            <span className="text-gray-600">Loading preview...</span>
                        </div>
                    </div>
                )}

                {/* Preview */}
                {preview && !loadingPreview && (
                    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-semibold text-gray-900">Onboarding Preview</h2>
                            <button
                                onClick={loadPreview}
                                className="text-sm text-blue-600 hover:text-blue-800"
                                disabled={loadingPreview}
                            >
                                Refresh
                            </button>
                        </div>

                        {/* Validation Errors */}
                        {hasValidationErrors && (
                            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
                                <div className="flex items-center mb-2">
                                    <XCircle className="h-5 w-5 text-red-600 mr-2" />
                                    <h3 className="font-semibold text-red-900">Validation Errors</h3>
                                </div>
                                <ul className="list-disc list-inside text-sm text-red-700">
                                    {preview.validationErrors.map((error, idx) => (
                                        <li key={idx}>{error}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Warnings */}
                        {hasWarnings && (
                            <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                                <div className="flex items-center mb-2">
                                    <AlertTriangle className="h-5 w-5 text-yellow-600 mr-2" />
                                    <h3 className="font-semibold text-yellow-900">Warnings</h3>
                                </div>
                                <ul className="list-disc list-inside text-sm text-yellow-700">
                                    {preview.warnings.map((warning, idx) => (
                                        <li key={idx}>{warning}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Summary Stats */}
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                            <div className="bg-blue-50 p-4 rounded-lg">
                                <div className="flex items-center mb-2">
                                    <Building2 className="h-5 w-5 text-blue-600 mr-2" />
                                    <span className="text-sm font-medium text-blue-900">Locations</span>
                                </div>
                                <div className="text-2xl font-bold text-blue-900">{preview.totalLocations}</div>
                            </div>
                            <div className="bg-green-50 p-4 rounded-lg">
                                <div className="flex items-center mb-2">
                                    <Tag className="h-5 w-5 text-green-600 mr-2" />
                                    <span className="text-sm font-medium text-green-900">Brands</span>
                                </div>
                                <div className="text-2xl font-bold text-green-900">{preview.totalBrands}</div>
                            </div>
                            <div className="bg-purple-50 p-4 rounded-lg">
                                <div className="flex items-center mb-2">
                                    <FileText className="h-5 w-5 text-purple-600 mr-2" />
                                    <span className="text-sm font-medium text-purple-900">Templates</span>
                                </div>
                                <div className="text-2xl font-bold text-purple-900">{preview.totalTemplates}</div>
                            </div>
                            <div className="bg-orange-50 p-4 rounded-lg">
                                <div className="flex items-center mb-2">
                                    <MapPin className="h-5 w-5 text-orange-600 mr-2" />
                                    <span className="text-sm font-medium text-orange-900">Messages</span>
                                </div>
                                <div className="text-2xl font-bold text-orange-900">{preview.estimatedMessages}</div>
                            </div>
                        </div>

                        {/* VoiceBox Mapping Status */}
                        <div className="mb-4 p-3 bg-gray-50 rounded-md">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                    {preview.hasVoiceBoxMapping ? (
                                        <>
                                            <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                                            <span className="text-sm text-gray-700">VoiceBox mapping configured - Ready to push</span>
                                        </>
                                    ) : (
                                        <>
                                            <AlertTriangle className="h-5 w-5 text-yellow-600 mr-2" />
                                            <span className="text-sm text-yellow-700">No VoiceBox mapping - Can create messages locally only</span>
                                        </>
                                    )}
                                </div>
                            </div>
                            {!preview.hasVoiceBoxMapping && (
                                <p className="text-xs text-gray-600 mt-2 ml-7">
                                    Configure VoiceBox mapping on the VoiceBox Sync page to enable pushing messages to VoiceBox.
                                </p>
                            )}
                        </div>

                        {/* Location Details */}
                        {preview.locations && preview.locations.length > 0 && (
                            <div className="mt-6">
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="text-lg font-semibold text-gray-900">Location Details</h3>
                                    <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                        Each location may have multiple brands
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    {preview.locations.map((location) => (
                                        <div key={location.locationId} className="border border-gray-200 rounded-lg">
                                            <button
                                                onClick={() => toggleLocationExpanded(location.locationId)}
                                                className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
                                            >
                                                <div className="flex items-center">
                                                    <MapPin className="h-5 w-5 text-gray-500 mr-2" />
                                                    <span className="font-medium text-gray-900">{location.locationName}</span>
                                                    <span className="ml-3 text-sm text-gray-500">
                                                        {location.brandCount} brand{location.brandCount !== 1 ? 's' : ''} • {location.templateCount} template{location.templateCount !== 1 ? 's' : ''} • ~{location.estimatedMessages} message{location.estimatedMessages !== 1 ? 's' : ''}
                                                    </span>
                                                    {location.brandCount > 1 && (
                                                        <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                                                            Multiple Brands
                                                        </span>
                                                    )}
                                                </div>
                                                {expandedLocations.has(location.locationId) ? (
                                                    <ChevronUp className="h-5 w-5 text-gray-500" />
                                                ) : (
                                                    <ChevronDown className="h-5 w-5 text-gray-500" />
                                                )}
                                            </button>
                                            {expandedLocations.has(location.locationId) && (
                                                <div className="px-4 pb-4 pt-2 bg-gray-50">
                                                    {location.brands && location.brands.length > 0 ? (
                                                        <div className="space-y-3">
                                                            {location.brands.length > 1 && (
                                                                <p className="text-xs text-gray-600 mb-2 italic">
                                                                    This location has {location.brands.length} brand{location.brands.length !== 1 ? 's' : ''} associated with it. Messages will be created for each brand.
                                                                </p>
                                                            )}
                                                            {location.brands.map((brand) => (
                                                                <div key={brand.brandId} className="pl-4 border-l-2 border-blue-200">
                                                                    <div className="flex items-center mb-2">
                                                                        <Tag className="h-4 w-4 text-blue-600 mr-2" />
                                                                        <span className="font-medium text-gray-900">{brand.brandName}</span>
                                                                        <span className="ml-2 text-sm text-gray-500">
                                                                            ({brand.templateCount} template{brand.templateCount !== 1 ? 's' : ''})
                                                                        </span>
                                                                    </div>
                                                                    {brand.templates && brand.templates.length > 0 && (
                                                                        <ul className="ml-6 text-sm text-gray-600 space-y-1">
                                                                            {brand.templates.map((template) => (
                                                                                <li key={template.id} className="flex items-center">
                                                                                    <FileText className="h-3 w-3 text-gray-400 mr-2" />
                                                                                    <span>{template.message_name}</span>
                                                                                    <span className="ml-2 text-xs text-gray-400">({template.message_type})</span>
                                                                                </li>
                                                                            ))}
                                                                        </ul>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <p className="text-sm text-gray-500 italic">No brands associated with this location</p>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Two-Step Process */}
                        <div className="mt-6 pt-6 border-t border-gray-200">
                            <div className="mb-4">
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">Onboarding Steps</h3>
                                <div className="space-y-3">
                                    {/* Step 1: Create Messages Locally */}
                                    <div className="border border-gray-200 rounded-lg p-4">
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="flex items-center">
                                                <div className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-semibold mr-3">
                                                    1
                                                </div>
                                                <div>
                                                    <h4 className="font-medium text-gray-900">Create Messages Locally</h4>
                                                    <p className="text-sm text-gray-600">Store messages in local database (no VoiceBox required)</p>
                                                </div>
                                            </div>
                                            {createMessagesResults?.success && (
                                                <CheckCircle className="h-5 w-5 text-green-600" />
                                            )}
                                        </div>
                                        <button
                                            onClick={handleCreateMessages}
                                            disabled={!canOnboard || creatingMessages || onboarding || pushingToVoicebox}
                                            className={`w-full px-4 py-2 rounded-md font-medium transition-colors ${
                                                canOnboard && !creatingMessages && !onboarding && !pushingToVoicebox
                                                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                            }`}
                                        >
                                            {creatingMessages ? (
                                                <>
                                                    <Loader className="inline-block animate-spin h-4 w-4 mr-2" />
                                                    Creating messages...
                                                </>
                                            ) : (
                                                <>
                                                    <FileText className="inline-block h-4 w-4 mr-2" />
                                                    Create Messages Locally
                                                </>
                                            )}
                                        </button>
                                    </div>

                                    {/* Step 2: Push to VoiceBox */}
                                    <div className={`border rounded-lg p-4 ${preview.hasVoiceBoxMapping ? 'border-gray-200' : 'border-gray-200 bg-gray-50'}`}>
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="flex items-center">
                                                <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-semibold mr-3 ${
                                                    preview.hasVoiceBoxMapping 
                                                        ? createMessagesResults?.success 
                                                            ? 'bg-green-100 text-green-600' 
                                                            : 'bg-gray-100 text-gray-400'
                                                        : 'bg-gray-100 text-gray-400'
                                                }`}>
                                                    2
                                                </div>
                                                <div>
                                                    <h4 className="font-medium text-gray-900">Push to VoiceBox</h4>
                                                    <p className="text-sm text-gray-600">
                                                        {preview.hasVoiceBoxMapping 
                                                            ? 'Push created messages to VoiceBox database'
                                                            : 'Requires VoiceBox mapping to be configured first'}
                                                    </p>
                                                </div>
                                            </div>
                                            {pushToVoiceboxResults?.success && (
                                                <CheckCircle className="h-5 w-5 text-green-600" />
                                            )}
                                        </div>
                                        <button
                                            onClick={() => setShowPushConfirm(true)}
                                            disabled={!canOnboard || !preview.hasVoiceBoxMapping || !createMessagesResults?.success || pushingToVoicebox || onboarding || creatingMessages}
                                            className={`w-full px-4 py-2 rounded-md font-medium transition-colors ${
                                                canOnboard && preview.hasVoiceBoxMapping && createMessagesResults?.success && !pushingToVoicebox && !onboarding && !creatingMessages
                                                    ? 'bg-green-600 text-white hover:bg-green-700'
                                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                            }`}
                                        >
                                            {pushingToVoicebox ? (
                                                <>
                                                    <Loader className="inline-block animate-spin h-4 w-4 mr-2" />
                                                    Pushing to VoiceBox...
                                                </>
                                            ) : (
                                                <>
                                                    <Play className="inline-block h-4 w-4 mr-2" />
                                                    Push to VoiceBox
                                                </>
                                            )}
                                        </button>
                                        {!preview.hasVoiceBoxMapping && (
                                            <p className="text-xs text-gray-500 mt-2">
                                                Configure VoiceBox mapping on the VoiceBox Sync page first.
                                            </p>
                                        )}
                                        {preview.hasVoiceBoxMapping && !createMessagesResults?.success && (
                                            <p className="text-xs text-gray-500 mt-2">
                                                Complete Step 1 first to create messages locally.
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Overwrite Option */}
                            <div className="flex items-center mb-4 pt-4 border-t border-gray-200">
                                <input
                                    type="checkbox"
                                    id="overwrite"
                                    checked={overwriteExisting}
                                    onChange={(e) => setOverwriteExisting(e.target.checked)}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    disabled={onboarding || creatingMessages || pushingToVoicebox}
                                />
                                <label htmlFor="overwrite" className="ml-2 text-sm text-gray-700">
                                    Overwrite existing messages
                                </label>
                            </div>

                            {/* One-Step Option (Legacy) */}
                            <div className="pt-4 border-t border-gray-200">
                                <details className="group">
                                    <summary className="cursor-pointer text-sm text-gray-600 hover:text-gray-900">
                                        Advanced: One-step onboarding (creates and pushes in one action)
                                    </summary>
                                    <div className="mt-3">
                                        <button
                                            onClick={() => setShowOnboardConfirm(true)}
                                            disabled={!canOnboard || !preview.hasVoiceBoxMapping || onboarding || creatingMessages || pushingToVoicebox}
                                            className={`w-full px-4 py-2 rounded-md font-medium transition-colors ${
                                                canOnboard && preview.hasVoiceBoxMapping && !onboarding && !creatingMessages && !pushingToVoicebox
                                                    ? 'bg-purple-600 text-white hover:bg-purple-700'
                                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                            }`}
                                        >
                                            {onboarding ? (
                                                <>
                                                    <Loader className="inline-block animate-spin h-4 w-4 mr-2" />
                                                    Onboarding in progress...
                                                </>
                                            ) : (
                                                <>
                                                    <Play className="inline-block h-4 w-4 mr-2" />
                                                    Create & Push in One Step
                                                </>
                                            )}
                                        </button>
                                        {!preview.hasVoiceBoxMapping && (
                                            <p className="text-xs text-gray-500 mt-2">
                                                Requires VoiceBox mapping to be configured.
                                            </p>
                                        )}
                                    </div>
                                </details>
                            </div>
                        </div>
                    </div>
                )}

                {/* Create Messages Results */}
                {createMessagesResults && (
                    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                        <h2 className="text-xl font-semibold text-gray-900 mb-4">Step 1: Create Messages Results</h2>
                        {createMessagesResults.success ? (
                            <div className="space-y-4">
                                <div className="p-4 bg-green-50 border border-green-200 rounded-md">
                                    <div className="flex items-center mb-2">
                                        <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                                        <span className="font-semibold text-green-900">Messages Created Successfully</span>
                                    </div>
                                    <p className="text-sm text-green-700">{createMessagesResults.message}</p>
                                    {createMessagesResults.note && (
                                        <p className="text-xs text-green-600 mt-2 italic">{createMessagesResults.note}</p>
                                    )}
                                </div>
                                {createMessagesResults.results && (
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div className="p-4 bg-blue-50 rounded-lg">
                                            <div className="text-sm font-medium text-blue-900 mb-1">Locations Processed</div>
                                            <div className="text-2xl font-bold text-blue-900">{createMessagesResults.results.locationsProcessed}</div>
                                        </div>
                                        <div className="p-4 bg-green-50 rounded-lg">
                                            <div className="text-sm font-medium text-green-900 mb-1">Messages Created</div>
                                            <div className="text-2xl font-bold text-green-900">{createMessagesResults.results.messagesCreated}</div>
                                        </div>
                                        <div className="p-4 bg-purple-50 rounded-lg">
                                            <div className="text-sm font-medium text-purple-900 mb-1">Messages Updated</div>
                                            <div className="text-2xl font-bold text-purple-900">{createMessagesResults.results.messagesUpdated}</div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                                <div className="flex items-center mb-2">
                                    <XCircle className="h-5 w-5 text-red-600 mr-2" />
                                    <span className="font-semibold text-red-900">Failed to Create Messages</span>
                                </div>
                                <p className="text-sm text-red-700 mb-2">{createMessagesResults.error}</p>
                                {createMessagesResults.validationErrors && createMessagesResults.validationErrors.length > 0 && (
                                    <ul className="list-disc list-inside text-sm text-red-700">
                                        {createMessagesResults.validationErrors.map((error, idx) => (
                                            <li key={idx}>{error}</li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Push to VoiceBox Results */}
                {pushToVoiceboxResults && (
                    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                        <h2 className="text-xl font-semibold text-gray-900 mb-4">Step 2: Push to VoiceBox Results</h2>
                        {pushToVoiceboxResults.success ? (
                            <div className="space-y-4">
                                <div className="p-4 bg-green-50 border border-green-200 rounded-md">
                                    <div className="flex items-center mb-2">
                                        <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                                        <span className="font-semibold text-green-900">Messages Pushed Successfully</span>
                                    </div>
                                    <p className="text-sm text-green-700">{pushToVoiceboxResults.message}</p>
                                </div>
                                {pushToVoiceboxResults.results && (
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div className="p-4 bg-blue-50 rounded-lg">
                                            <div className="text-sm font-medium text-blue-900 mb-1">Locations Processed</div>
                                            <div className="text-2xl font-bold text-blue-900">{pushToVoiceboxResults.results.locationsProcessed}</div>
                                        </div>
                                        <div className="p-4 bg-green-50 rounded-lg">
                                            <div className="text-sm font-medium text-green-900 mb-1">Messages Created</div>
                                            <div className="text-2xl font-bold text-green-900">{pushToVoiceboxResults.results.messagesCreated}</div>
                                        </div>
                                        <div className="p-4 bg-purple-50 rounded-lg">
                                            <div className="text-sm font-medium text-purple-900 mb-1">Messages Updated</div>
                                            <div className="text-2xl font-bold text-purple-900">{pushToVoiceboxResults.results.messagesUpdated}</div>
                                        </div>
                                    </div>
                                )}
                                {pushToVoiceboxResults.warning && (
                                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                                        <p className="text-sm text-yellow-800">{pushToVoiceboxResults.warning}</p>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                                <div className="flex items-center mb-2">
                                    <XCircle className="h-5 w-5 text-red-600 mr-2" />
                                    <span className="font-semibold text-red-900">Failed to Push to VoiceBox</span>
                                </div>
                                <p className="text-sm text-red-700 mb-2">{pushToVoiceboxResults.error}</p>
                                {pushToVoiceboxResults.validationErrors && pushToVoiceboxResults.validationErrors.length > 0 && (
                                    <ul className="list-disc list-inside text-sm text-red-700">
                                        {pushToVoiceboxResults.validationErrors.map((error, idx) => (
                                            <li key={idx}>{error}</li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Onboarding Results (One-Step) */}
                {onboardingResults && (
                    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                        <h2 className="text-xl font-semibold text-gray-900 mb-4">Onboarding Results</h2>
                        {onboardingResults.success ? (
                            <div className="space-y-4">
                                <div className="p-4 bg-green-50 border border-green-200 rounded-md">
                                    <div className="flex items-center mb-2">
                                        <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                                        <span className="font-semibold text-green-900">Onboarding Completed Successfully</span>
                                    </div>
                                    <p className="text-sm text-green-700">{onboardingResults.message}</p>
                                </div>
                                {onboardingResults.results && (
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div className="p-4 bg-blue-50 rounded-lg">
                                            <div className="text-sm font-medium text-blue-900 mb-1">Locations Processed</div>
                                            <div className="text-2xl font-bold text-blue-900">{onboardingResults.results.locationsProcessed}</div>
                                        </div>
                                        <div className="p-4 bg-green-50 rounded-lg">
                                            <div className="text-sm font-medium text-green-900 mb-1">Messages Created</div>
                                            <div className="text-2xl font-bold text-green-900">{onboardingResults.results.messagesCreated}</div>
                                        </div>
                                        <div className="p-4 bg-purple-50 rounded-lg">
                                            <div className="text-sm font-medium text-purple-900 mb-1">Messages Updated</div>
                                            <div className="text-2xl font-bold text-purple-900">{onboardingResults.results.messagesUpdated}</div>
                                        </div>
                                    </div>
                                )}
                                {onboardingResults.results?.warnings && onboardingResults.results.warnings.length > 0 && (
                                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                                        <div className="flex items-center mb-2">
                                            <AlertTriangle className="h-5 w-5 text-yellow-600 mr-2" />
                                            <span className="font-semibold text-yellow-900">Warnings</span>
                                        </div>
                                        <ul className="list-disc list-inside text-sm text-yellow-700">
                                            {onboardingResults.results.warnings.map((warning, idx) => (
                                                <li key={idx}>{warning}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                                <div className="flex items-center mb-2">
                                    <XCircle className="h-5 w-5 text-red-600 mr-2" />
                                    <span className="font-semibold text-red-900">Onboarding Failed</span>
                                </div>
                                <p className="text-sm text-red-700 mb-2">{onboardingResults.error}</p>
                                {onboardingResults.validationErrors && onboardingResults.validationErrors.length > 0 && (
                                    <ul className="list-disc list-inside text-sm text-red-700">
                                        {onboardingResults.validationErrors.map((error, idx) => (
                                            <li key={idx}>{error}</li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Push to VoiceBox Confirmation Modal */}
                <Modal
                    isOpen={showPushConfirm}
                    onClose={() => setShowPushConfirm(false)}
                    title="Confirm Push to VoiceBox"
                >
                    <div className="space-y-4">
                        <div className="p-4 bg-red-50 border-2 border-red-300 rounded-md">
                            <div className="flex items-center mb-2">
                                <AlertTriangle className="h-5 w-5 text-red-600 mr-2" />
                                <p className="font-semibold text-red-900">VoiceBox Database Update</p>
                            </div>
                            <p className="text-sm text-red-800 mb-2">
                                This will <strong>permanently modify</strong> the VoiceBox database by creating folders and messages.
                            </p>
                            <p className="text-sm text-red-800">
                                This action cannot be easily undone. Please ensure you have reviewed the preview above.
                            </p>
                        </div>
                        <p className="text-gray-700">
                            This will push locally created messages to VoiceBox for <strong>{preview?.groupName}</strong>.
                        </p>
                        {preview && (
                            <div className="bg-gray-50 p-4 rounded-md">
                                <ul className="text-sm text-gray-700 space-y-1">
                                    <li>• {preview.totalLocations} location(s)</li>
                                    <li>• Approximately {preview.estimatedMessages} message(s) will be pushed</li>
                                </ul>
                            </div>
                        )}
                        {overwriteExisting && (
                            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                                <p className="text-sm text-yellow-800">
                                    <strong>Warning:</strong> Existing messages in VoiceBox will be overwritten.
                                </p>
                            </div>
                        )}
                        <div className="flex justify-end space-x-3 pt-4">
                            <button
                                onClick={() => setShowPushConfirm(false)}
                                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handlePushToVoicebox}
                                className="px-4 py-2 text-white bg-green-600 rounded-md hover:bg-green-700"
                            >
                                Confirm & Push
                            </button>
                        </div>
                    </div>
                </Modal>

                {/* One-Step Onboarding Confirmation Modal */}
                <Modal
                    isOpen={showOnboardConfirm}
                    onClose={() => setShowOnboardConfirm(false)}
                    title="Confirm Onboarding"
                >
                    <div className="space-y-4">
                        <div className="p-4 bg-red-50 border-2 border-red-300 rounded-md">
                            <div className="flex items-center mb-2">
                                <AlertTriangle className="h-5 w-5 text-red-600 mr-2" />
                                <p className="font-semibold text-red-900">VoiceBox Database Update</p>
                            </div>
                            <p className="text-sm text-red-800 mb-2">
                                This will <strong>permanently modify</strong> the VoiceBox database by creating folders and messages.
                            </p>
                            <p className="text-sm text-red-800">
                                This action cannot be easily undone. Please ensure you have reviewed the preview above.
                            </p>
                        </div>
                        <p className="text-gray-700">
                            This will create location-specific messages in VoiceBox for all locations in <strong>{preview?.groupName}</strong>.
                        </p>
                        {preview && (
                            <div className="bg-gray-50 p-4 rounded-md">
                                <ul className="text-sm text-gray-700 space-y-1">
                                    <li>• {preview.totalLocations} location(s)</li>
                                    <li>• {preview.totalBrands} brand(s)</li>
                                    <li>• {preview.totalTemplates} template(s)</li>
                                    <li>• Approximately {preview.estimatedMessages} message(s) will be created</li>
                                </ul>
                            </div>
                        )}
                        {overwriteExisting && (
                            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                                <p className="text-sm text-yellow-800">
                                    <strong>Warning:</strong> Existing messages in VoiceBox will be overwritten.
                                </p>
                            </div>
                        )}
                        <div className="flex justify-end space-x-3 pt-4">
                            <button
                                onClick={() => setShowOnboardConfirm(false)}
                                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleOnboard}
                                className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700"
                            >
                                Confirm & Start
                            </button>
                        </div>
                    </div>
                </Modal>
            </div>
        </div>
    );
};

export default AdminDealershipOnboardingPage;
